/**
 * Every walkthrough video the app can show, by key. Videos are produced with
 * the `walkthrough-video` skill and live under public/guides/.
 *
 * A help article (app/help/**\/content.json) names a video by key in a
 * section's `video` field; a component that embeds one reads the same row.
 * One table means a re-rendered video changes one path, not every embed.
 *
 * `durationSeconds` is what `ffprobe` reports for the mp4, so a badge can say
 * how long the video is before anyone fetches it. Re-encode a video, re-read
 * the number (the skill's encode step prints it).
 *
 * Plain data, no imports: safe to read from server components, client
 * components and tests alike.
 */
export const WALKTHROUGH_VIDEOS = {
  "admin-people-and-team": {
    src: "/guides/admin/add-people-and-organize-your-team.mp4",
    poster: "/guides/admin/add-people-and-organize-your-team-poster.jpg",
    title: "add people and organize your team",
    durationSeconds: 85,
  },
  "admin-rubric-scenario": {
    src: "/guides/admin/configure-a-rubric-and-scenario.mp4",
    poster: "/guides/admin/configure-a-rubric-and-scenario-poster.jpg",
    title: "configure a rubric and scenario",
    durationSeconds: 75,
  },
  "assigned-training": {
    src: "/guides/training/find-and-finish-assigned-training.mp4",
    poster: "/guides/training/find-and-finish-assigned-training-poster.jpg",
    title: "find and finish your assigned training",
    durationSeconds: 52,
  },
  "understand-your-score": {
    src: "/guides/member/understand-your-score.mp4",
    poster: "/guides/member/understand-your-score-poster.jpg",
    title: "understand your score and find past results",
    durationSeconds: 85,
  },
  "retake-a-test": {
    src: "/guides/member/retake-a-test.mp4",
    poster: "/guides/member/retake-a-test-poster.jpg",
    title: "retake a test",
    durationSeconds: 77,
  },
  "microphone-setup": {
    src: "/guides/simulator/get-your-microphone-working.mp4",
    poster: "/guides/simulator/get-your-microphone-working-poster.jpg",
    title: "get your microphone working",
    durationSeconds: 84,
  },
  "first-practice-session": {
    src: "/guides/simulator/first-practice-session.mp4",
    poster: "/guides/simulator/first-practice-session-poster.jpg",
    title: "run a practice session",
    durationSeconds: 85,
  },
  "first-scored-session": {
    src: "/guides/simulator/first-scored-session.mp4",
    poster: "/guides/simulator/first-scored-session-poster.jpg",
    title: "run a scored session",
    durationSeconds: 82,
  },
  "calendar-auto-join": {
    src: "/guides/integrations/calendar-auto-join.mp4",
    poster: "/guides/integrations/calendar-auto-join-poster.jpg",
    title: "connect your calendar so the Notetaker joins your meetings",
    durationSeconds: 81,
  },
  "claude-setup": {
    src: "/guides/agent-connections/salesably-claude-setup.mp4",
    poster: "/guides/agent-connections/salesably-claude-setup-poster.jpg",
    title: "add Salesably to Claude",
    durationSeconds: 99,
  },
  "claude-use": {
    src: "/guides/agent-connections/salesably-claude-use.mp4",
    poster: "/guides/agent-connections/salesably-claude-use-poster.jpg",
    title: "run your studio from Claude",
    durationSeconds: 89,
  },
  "chatgpt-setup": {
    src: "/guides/agent-connections/salesably-chatgpt-setup.mp4",
    poster: "/guides/agent-connections/salesably-chatgpt-setup-poster.jpg",
    title: "set up and use Salesably in ChatGPT",
    durationSeconds: 117,
  },
  "chatgpt-use": {
    src: "/guides/agent-connections/salesably-chatgpt-use.mp4",
    poster: "/guides/agent-connections/salesably-chatgpt-use-poster.jpg",
    title: "run your studio from ChatGPT",
    durationSeconds: 83,
  },
};

/**
 * @param {string} key
 * @returns {{ src: string, poster: string, title: string, durationSeconds: number }}
 * @throws when the key names no video — a typo in a help article should fail
 *   the build, not render an empty player.
 */
export function getWalkthroughVideo(key) {
  const video = WALKTHROUGH_VIDEOS[key];
  if (!video) throw new Error(`Unknown walkthrough video: ${key}`);
  return video;
}
